import { useState, useRef, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Youtube } from "@tiptap/extension-youtube";
import {
  Bold, Italic, List, ListOrdered, Quote, Heading2, Heading3,
  Link as LinkIcon, Image as ImageIcon, Undo, Redo,
  Table as TableIcon, Youtube as YoutubeIcon, Upload,
  Rows3, Columns3, X as XIcon, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import GalleryPickerDialog from "./GalleryPickerDialog";

/** Tiptap-based rich text editor → emits HTML on change. Supports:
 * - Headings, lists, quotes, links, bold/italic (StarterKit)
 * - Tables with row/col controls
 * - YouTube embeds
 * - Inline image upload (drag/drop, paste, file picker) → object storage
 * - "Insert from Gallery" dialog
 * - Live word count + reading-time badge
 */
export default function BlogEditor({ value, onChange }) {
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);
  const fileInputRef = useRef(null);

  const uploadFile = useCallback(async (file) => {
    if (!file || !file.type?.startsWith("image/")) {
      toast.error("Only images can be uploaded here");
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/blog/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return `${API}${data.url}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      return null;
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false, link: false }),
      Link.configure({
        openOnClick: false, autolink: true,
        HTMLAttributes: { class: "text-gg-green underline" },
      }),
      Image.configure({ HTMLAttributes: { class: "rounded-xl my-4 max-w-full" } }),
      Placeholder.configure({
        placeholder: "Start writing… drag an image in, use the toolbar for tables/embeds, or press Ctrl+Z to undo.",
      }),
      CharacterCount.configure({}),
      Table.configure({ resizable: false, HTMLAttributes: { class: "gg-table" } }),
      TableRow,
      TableHeader,
      TableCell,
      Youtube.configure({
        controls: true, nocookie: true,
        HTMLAttributes: { class: "rounded-xl overflow-hidden my-6 aspect-video w-full" },
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[380px] px-4 py-3",
        "data-testid": "blog-editor-content",
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items || [];
        for (const item of items) {
          if (item.type?.startsWith("image/")) {
            const file = item.getAsFile();
            if (!file) continue;
            event.preventDefault();
            uploadFile(file).then((url) => {
              if (url) editor?.chain().focus().setImage({ src: url }).run();
            });
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        const imageFiles = Array.from(files).filter((f) => f.type?.startsWith("image/"));
        if (imageFiles.length === 0) return false;
        event.preventDefault();
        Promise.all(imageFiles.map(uploadFile)).then((urls) => {
          urls.filter(Boolean).forEach((url) => {
            editor?.chain().focus().setImage({ src: url }).run();
          });
        });
        return true;
      },
    },
  });

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("Link URL (leave empty to remove)", previous || "");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
  };

  const insertYoutube = () => {
    const url = window.prompt("Paste a YouTube URL");
    if (!url) return;
    editor.commands.setYoutubeVideo({ src: url.trim() });
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const insertGalleryImage = (photo) => {
    if (!photo?.url) return;
    editor.chain().focus().setImage({ src: photo.url, alt: photo.alt_text || photo.caption || "" }).run();
    setGalleryPickerOpen(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // reset so same file can be reselected later
    const url = await uploadFile(file);
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const Btn = ({ active, onClick, label, children, testid, disabled }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      disabled={disabled}
      data-testid={testid}
      className={`p-1.5 rounded-md transition-colors disabled:opacity-30 ${
        active ? "bg-gg-green/15 text-gg-green" : "text-gg-brown hover:bg-gg-bg"
      }`}
    >
      {children}
    </button>
  );

  const words = editor.storage.characterCount?.words?.() || 0;
  const readingMin = Math.max(1, Math.round(words / 220));
  const tableActive = editor.isActive("table");

  return (
    <div className="rounded-xl border border-gg-border bg-gg-surface overflow-hidden" data-testid="blog-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-gg-border bg-gg-bg px-2 py-1.5">
        <Btn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} label="Heading 2"><Heading2 size={16} /></Btn>
        <Btn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} label="Heading 3"><Heading3 size={16} /></Btn>
        <span className="mx-1 w-px h-5 bg-gg-border" />
        <Btn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold" testid="blog-bold"><Bold size={16} /></Btn>
        <Btn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic"><Italic size={16} /></Btn>
        <span className="mx-1 w-px h-5 bg-gg-border" />
        <Btn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Bulleted list"><List size={16} /></Btn>
        <Btn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Numbered list"><ListOrdered size={16} /></Btn>
        <Btn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} label="Quote"><Quote size={16} /></Btn>
        <span className="mx-1 w-px h-5 bg-gg-border" />
        <Btn active={editor.isActive("link")} onClick={setLink} label="Link"><LinkIcon size={16} /></Btn>
        <Btn onClick={() => fileInputRef.current?.click()} label="Upload image" testid="blog-upload-image"><Upload size={16} /></Btn>
        <Btn onClick={() => setGalleryPickerOpen(true)} label="Insert from Gallery" testid="blog-gallery-pick"><ImageIcon size={16} /></Btn>
        <Btn onClick={insertYoutube} label="Embed YouTube" testid="blog-insert-youtube"><YoutubeIcon size={16} /></Btn>
        <Btn onClick={insertTable} label="Insert table" testid="blog-insert-table"><TableIcon size={16} /></Btn>
        <span className="mx-1 w-px h-5 bg-gg-border" />
        <Btn onClick={() => editor.chain().focus().undo().run()} label="Undo"><Undo size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} label="Redo"><Redo size={16} /></Btn>
        {/* Table controls (visible only when cursor is inside a table) */}
        {tableActive && (
          <>
            <span className="mx-1 w-px h-5 bg-gg-border" />
            <span className="text-[10px] uppercase text-gg-brown font-semibold px-1">Table:</span>
            <Btn onClick={() => editor.chain().focus().addRowAfter().run()} label="Add row below" testid="blog-table-add-row"><Rows3 size={14} /><Plus size={10} /></Btn>
            <Btn onClick={() => editor.chain().focus().addColumnAfter().run()} label="Add col right"><Columns3 size={14} /><Plus size={10} /></Btn>
            <Btn onClick={() => editor.chain().focus().deleteRow().run()} label="Delete row"><Rows3 size={14} /><XIcon size={10} /></Btn>
            <Btn onClick={() => editor.chain().focus().deleteColumn().run()} label="Delete col"><Columns3 size={14} /><XIcon size={10} /></Btn>
            <Btn onClick={() => editor.chain().focus().deleteTable().run()} label="Delete table"><XIcon size={14} /></Btn>
          </>
        )}
        {/* Word count + reading time — right-aligned */}
        <div className="ml-auto flex items-center gap-3 text-[11px] text-gg-muted pr-1" data-testid="blog-editor-stats">
          <span><strong className="text-gg-brown">{words.toLocaleString()}</strong> words</span>
          <span><strong className="text-gg-brown">{readingMin}</strong> min read</span>
        </div>
      </div>
      <EditorContent editor={editor} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileUpload}
        data-testid="blog-image-file-input"
      />
      {galleryPickerOpen && (
        <GalleryPickerDialog
          onClose={() => setGalleryPickerOpen(false)}
          onSelect={insertGalleryImage}
        />
      )}
    </div>
  );
}
